import React from 'react';
import { Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import HealWoundPopover from './HealWoundPopover';
import type { WoundResponse } from '../../services/api';
import { formatWoundDate, getWoundTypeLabel } from '../../constants/wounds';

interface WoundTableRowProps {
  wound: WoundResponse;
  assessmentCount: number;
  canEdit?: boolean;
  canDiscontinue?: boolean;
  onEdit: (wound: WoundResponse) => void;
  onDelete: (wound: WoundResponse) => void;
  onDiscontinue: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
  /** 'row' renders a desktop table row; 'card' renders a mobile stacked card. */
  variant?: 'row' | 'card';
}

const WoundTableRow: React.FC<WoundTableRowProps> = ({
  wound,
  assessmentCount,
  canEdit = true,
  canDiscontinue = false,
  onEdit,
  onDelete,
  onDiscontinue,
  onOpenAssessments,
  variant = 'row',
}) => {
  const isDiscontinued = !!wound.discontinued_date;

  // Shared rendering pieces reused by both the desktop row and the mobile card
  // so handlers stay identical across variants.
  const statusBadge = isDiscontinued ? (
    <Badge variant='secondary' className='text-xs'>
      Discontinued
    </Badge>
  ) : (
    <Badge
      variant='outline'
      className='text-xs bg-green-50 text-green-700 border-green-200'
    >
      Active
    </Badge>
  );

  const woundDetails = (
    <div className='space-y-0.5'>
      <p
        className={
          isDiscontinued ? 'italic text-muted-foreground' : 'font-medium'
        }
      >
        {wound.location}
      </p>
      <p className='text-xs text-muted-foreground'>
        {getWoundTypeLabel(wound.wound_type)}
      </p>
    </div>
  );

  const assessmentsButton = (
    <Button
      variant='outline'
      size='sm'
      onClick={() => onOpenAssessments(wound)}
    >
      Assessments ({assessmentCount})
    </Button>
  );

  const actions = (
    <div className='flex flex-wrap items-center gap-1.5'>
      {!isDiscontinued && canDiscontinue && (
        <HealWoundPopover
          minDate={new Date(wound.created_at)}
          onDiscontinue={(date, reason, comments) =>
            onDiscontinue(wound.id, date, reason, comments)
          }
          triggerLabel='Discontinue'
        />
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onEdit(wound)}
                disabled={!canEdit}
              >
                Edit
              </Button>
            </span>
          </TooltipTrigger>
          {!canEdit && (
            <TooltipContent>
              <p>Cannot edit because the visit is not in an editable state</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onDelete(wound)}
                disabled={!canEdit || assessmentCount > 0}
              >
                <Trash2 className='h-4 w-4 text-destructive mr-1' />
                Delete
              </Button>
            </span>
          </TooltipTrigger>
          {(!canEdit || assessmentCount > 0) && (
            <TooltipContent>
              <p>
                {!canEdit
                  ? 'Cannot edit because the visit is not in an editable state'
                  : 'Cannot delete a wound with assessments'}
              </p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  if (variant === 'card') {
    return (
      <div
        className={`rounded-md border p-3 space-y-3 ${
          isDiscontinued ? 'opacity-60' : ''
        }`}
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='min-w-0'>{woundDetails}</div>
          {statusBadge}
        </div>
        <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Onset</span>
            <span className='text-muted-foreground'>
              {formatWoundDate(wound.onset_date)}
            </span>
          </div>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Assessments</span>
            <span>{assessmentsButton}</span>
          </div>
        </div>
        <div className='flex flex-wrap justify-end gap-1.5 border-t pt-2'>
          {actions}
        </div>
      </div>
    );
  }

  return (
    <TableRow className={isDiscontinued ? 'opacity-60' : undefined}>
      <TableCell>{statusBadge}</TableCell>
      <TableCell className='text-sm'>{woundDetails}</TableCell>
      <TableCell className='text-sm text-muted-foreground'>
        {formatWoundDate(wound.onset_date)}
      </TableCell>
      <TableCell>{assessmentsButton}</TableCell>
      <TableCell>{actions}</TableCell>
    </TableRow>
  );
};

export default WoundTableRow;
